import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAppDispatch } from "./hooks/redux";
import { verifyTokenAction } from "./store/slices/auth/authSliceActions";
import Homepage from "./components/Homepage";
import Support from "./components/Support";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import GoogleAnalytics from "./components/GoogleAnalytics";
import {
  AdminLogin,
  AdminOverview,
  UserDetail,
  ProtectedAdminRoute,
  AdminAnalytics,
} from "./components/admin";
import { colors } from "./constants/colors";
import { store } from "./store";
import "./App.css";

// Inner component that uses Redux hooks
const AppContent = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const dispatch = useAppDispatch();

  // Verify token on app startup
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");

    if (storedToken) {
      // Always verify token on app startup if one exists
      dispatch(verifyTokenAction());
    }
  }, [dispatch]); // Only run once on mount

  const navStyle = {
    backgroundColor: colors.white,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  };

  const navContainerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem 2rem",
    position: "relative",
  };

  const logoStyle = {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: colors.darkGreen,
    textDecoration: "none",
  };

  const linkStyle = {
    color: colors.darkGray,
    textDecoration: "none",
    fontWeight: 500,
    transition: "color 0.3s ease",
  };

  return (
    <Router>
      <GoogleAnalytics />
      <div className="App">
        <nav style={navStyle}>
          <div style={navContainerStyle}>
            <Link to="/" style={logoStyle}>
              NutriPlate
            </Link>

            {/* Desktop Navigation */}
            <ul
              className="nav-links"
              style={{
                display: "flex",
                gap: "2rem",
                alignItems: "center",
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              <li>
                <Link
                  to="/"
                  style={linkStyle}
                  onMouseOver={(e) => (e.target.style.color = colors.darkGreen)}
                  onMouseOut={(e) => (e.target.style.color = colors.darkGray)}
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/support"
                  style={linkStyle}
                  onMouseOver={(e) => (e.target.style.color = colors.darkGreen)}
                  onMouseOut={(e) => (e.target.style.color = colors.darkGray)}
                >
                  Support
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  style={linkStyle}
                  onMouseOver={(e) => (e.target.style.color = colors.darkGreen)}
                  onMouseOut={(e) => (e.target.style.color = colors.darkGray)}
                >
                  Privacy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  style={linkStyle}
                  onMouseOver={(e) => (e.target.style.color = colors.darkGreen)}
                  onMouseOut={(e) => (e.target.style.color = colors.darkGray)}
                >
                  Terms
                </Link>
              </li>
            </ul>

            {/* Mobile Menu Button */}
            <button
              className="mobile-menu-button"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.darkGray,
              }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div
              className="mobile-menu"
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: colors.white,
                boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                padding: "1rem 2rem",
                gap: "1rem",
              }}
            >
              <Link
                to="/"
                style={linkStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/support"
                style={linkStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                Support
              </Link>
              <Link
                to="/privacy"
                style={linkStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                style={linkStyle}
                onClick={() => setIsMenuOpen(false)}
              >
                Terms
              </Link>
            </div>
          )}
        </nav>

        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route
            path="/admin/overview"
            element={
              <ProtectedAdminRoute>
                <AdminOverview />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/users/:uid"
            element={
              <ProtectedAdminRoute>
                <UserDetail />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedAdminRoute>
                <AdminAnalytics />
              </ProtectedAdminRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

// Main App component that provides Redux store
function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
